/**
 * upgradeTree — a large, gated upgrade/tech tree tuned for a slow, deliberate
 * progression.
 *
 *   • You start with 0 drones and a slow hull. Only the hull laser mines.
 *   • The whole first act (14 nodes across Laser / Optics / Engines / Refinery)
 *     is hull-only. Capping the mining + optics + engine lines unlocks the
 *     "Mothership Mk II" milestone (bigger MTS_2 hull) — which opens the Hangar
 *     & Drone bays. Drones are impossible before it.
 *   • A second act of drone upgrades (speed / cargo capacity / drills / more
 *     hangars) leads to the "Mothership Mk III" milestone (MTS_3), then an
 *     endgame tier.
 *
 * `computeMods` folds the purchased set into a single modifier bundle applied to
 * the game's base stats. Layout: `col` = branch column (0..4), `row` = tier.
 */
export interface UpgradeEffect {
  hangar?: number; // +N simultaneous drones
  mechDrones?: number; // +N mechanical (repair) drones
  beams?: number; // +N hull mining beams
  droneSpeedMul?: number;
  droneCargoMul?: number; // drone mineral capacity
  droneRateMul?: number; // drone mining rate
  droneRangeMul?: number; // drone deploy-circle radius
  shipSpeedMul?: number;
  solarSpeedMul?: number; // flight speed in the SOLAR layer only (not planet)
  rangeMul?: number; // hull laser range
  hullRateMul?: number; // hull mining rate (mining speed), per beam
  yieldMul?: number; // minerals gained
  fighterSlots?: number; // +N escort-fighter slots (Mk IV)
  fighterDrones?: number; // +N fighter-drone slots (Mk V Swarm-lord)
  missiles?: number; // +N homing-missile-salvo size (Mk V Swarm-lord Missile Pods)
  siloMissiles?: number; // +N homing-missile-salvo size (Mk VI Battleship Missile Silos — kept separate so the paths never share)
  turretRangeMul?: number; // hull turret firing/acquisition range (Mk V Battleship)
  hullHpBonus?: number; // +N flat max hull HP (Mk V Battleship)
  bigCannonBeamMul?: number; // Big-Cannon beam DURATION multiplier (Mk VI Battleship)
  omniMining?: boolean; // mine a full 360° circle instead of the front cone
  wormBodies?: number; // +N body segments (Mk VII Eater-of-Worlds hull)
  wormMissiles?: number; // +N red homing missiles per salvo (Mk VII)
  wormBeamDurMul?: number; // Mk VII maw-beam duration multiplier
  wormBeamDmgMul?: number; // Mk VII maw-beam damage multiplier
  branchChance?: number; // +chance a missile hit splits into branch missiles (SWL / worm)
  branchCount?: number; // +branch missiles per split
  // --- Mk VI Swarm-lord branches ---
  missileCdMul?: number; // × missile-salvo cooldown (Missile Cooldown nodes, <1 = faster)
  swlSpeedBonus?: number; // +fraction ship speed (additive; Swarm Thrusters)
  fighterMissiles?: boolean; // unlock: escort fighters auto-fire homing missiles
  droneMissiles?: boolean; // unlock: fighter drones fire homing missiles (1 per 15s)
  smartMissiles?: boolean; // missiles re-acquire a new target if the original dies
}

export interface UpgradeNode {
  id: string;
  name: string;
  desc: string;
  cost: number; // minerals
  sp?: number; // skill points also required
  units?: number; // Universal Units also required
  cores?: number; // energy cores also required (crafted at a mechanical planet)
  location?: "mechanical"; // only purchasable at a mechanical planet
  requires: string[]; // prerequisite node ids
  excludes?: string[]; // owning any of these locks this node (mutually-exclusive paths)
  col: number; // branch column (0..4)
  row: number; // tier row (0..10)
  effect: UpgradeEffect;
  milestone?: boolean; // pivotal gate node (styled specially)
  requiresItems?: Record<string, number>; // item ids (+counts) needed to buy — consumed on purchase, and the node stays HIDDEN until owned (secret refits)
  hiddenUntil?: string; // another node id that must be OWNED before this node appears (secret sequels)
}

export const UPGRADES: UpgradeNode[] = [
  // ============ ACT I — hull only (14 nodes, no drones) ============
  // Laser branch — mining speed (col 0)
  { id: "laser1", name: "Mining Laser I", desc: "Mining speed +25%", cost: 100, requires: [], col: 0, row: 0, effect: { hullRateMul: 1.25 } },
  { id: "laser2", name: "Mining Laser II", desc: "Mining speed +25%", cost: 220, requires: ["laser1"], col: 0, row: 1, effect: { hullRateMul: 1.25 } },
  { id: "laser3", name: "Mining Laser III", desc: "Mining speed +30%", cost: 400, requires: ["laser2"], col: 0, row: 2, effect: { hullRateMul: 1.3 } },
  { id: "laser4", name: "Focused Beam Array", desc: "Mining speed +30%", cost: 640, requires: ["laser3"], col: 0, row: 3, effect: { hullRateMul: 1.3 } },

  // Optics branch — mining range (col 1)
  { id: "optic1", name: "Focusing Optics I", desc: "Mining range +20%", cost: 110, requires: [], col: 1, row: 0, effect: { rangeMul: 1.2 } },
  { id: "optic2", name: "Focusing Optics II", desc: "Mining range +20%", cost: 230, requires: ["optic1"], col: 1, row: 1, effect: { rangeMul: 1.2 } },
  { id: "optic3", name: "Range Amplifier", desc: "Mining range +25%", cost: 410, requires: ["optic2"], col: 1, row: 2, effect: { rangeMul: 1.25 } },
  { id: "optic4", name: "Long-Range Lens", desc: "Mining range +25%", cost: 660, requires: ["optic3"], col: 1, row: 3, effect: { rangeMul: 1.25 } },

  // Engine branch — ship speed (col 3)
  { id: "engine1", name: "Ion Drive I", desc: "Ship speed +20%", cost: 130, requires: [], col: 3, row: 0, effect: { shipSpeedMul: 1.2 } },
  { id: "engine2", name: "Ion Drive II", desc: "Ship speed +20%", cost: 260, requires: ["engine1"], col: 3, row: 1, effect: { shipSpeedMul: 1.2 } },
  { id: "engine3", name: "Fusion Thrusters", desc: "Ship speed +25%", cost: 470, requires: ["engine2"], col: 3, row: 2, effect: { shipSpeedMul: 1.25 } },

  // Refinery branch — economy (col 4)
  { id: "refine1", name: "Ore Sorter", desc: "Mineral yield +15%", cost: 140, sp: 1, requires: [], col: 4, row: 0, effect: { yieldMul: 1.15 } },
  { id: "refine2", name: "Refinery I", desc: "Mineral yield +20%", cost: 300, sp: 1, requires: ["refine1"], col: 4, row: 1, effect: { yieldMul: 1.2 } },
  { id: "refine3", name: "Refinery II", desc: "Mineral yield +25%", cost: 540, sp: 1, requires: ["refine2"], col: 4, row: 2, effect: { yieldMul: 1.25 } },

  // Beam branch (col 2) — each adds a hull mining beam. Prerequisites sit one row
  // ABOVE each node so the connector lines always flow downward.
  { id: "beam1", name: "Tri-Beam Array", desc: "+1 mining beam (mechanical planet)", cost: 700, sp: 1, units: 2, location: "mechanical", requires: ["laser1"], col: 2, row: 1, effect: { beams: 1 } },
  { id: "beam2", name: "Quad-Beam Array", desc: "+1 mining beam (mechanical planet)", cost: 1800, sp: 1, units: 4, location: "mechanical", requires: ["beam1"], col: 2, row: 2, effect: { beams: 1 } },
  { id: "beam3", name: "Penta-Beam Array", desc: "+1 mining beam (mechanical planet)", cost: 4200, sp: 2, units: 6, location: "mechanical", requires: ["beam2"], col: 2, row: 3, effect: { beams: 1 } },

  // ============ MILESTONE — Mothership Mk II (MTS_2) ============
  {
    id: "mk2",
    name: "Mothership Mk II",
    desc: "Bigger hull: mining +30%, range +20%, speed −30%. Unlocks drone & repair bays.",
    cost: 20000,
    cores: 1,
    location: "mechanical",
    requires: ["laser4", "optic4", "engine3"],
    col: 2,
    row: 4,
    effect: { hullRateMul: 1.3, rangeMul: 1.2, shipSpeedMul: 0.7 },
    milestone: true,
  },

  // ============ ACT II — drones (must unlock Hangar Bay I first) ============
  // Row 5 — the drone gate. You must buy the 1st drone before ANY drone upgrade.
  { id: "hangar1", name: "Hangar Bay I", desc: "Unlocks drones: +1 drone", cost: 1400, requires: ["mk2"], col: 2, row: 5, effect: { hangar: 1 } },
  { id: "mechbay", name: "Repair Drone Bay", desc: "Unlocks mechanical repair drones: they repair the hull for minerals (3 per HP)", cost: 2000, requires: ["mk2"], col: 1, row: 5, effect: { mechDrones: 3 } },
  { id: "refine4", name: "Refinery III", desc: "Mineral yield +25%", cost: 1500, sp: 1, requires: ["mk2"], col: 4, row: 5, effect: { yieldMul: 1.25 } },

  // Row 6 — first drone upgrades, all gated on hangar1.
  { id: "dspeed1", name: "Drone Ion Thrusters", desc: "Drone speed +30%", cost: 1200, requires: ["hangar1"], col: 0, row: 6, effect: { droneSpeedMul: 1.3 } },
  { id: "dcargo1", name: "Cargo Pods I", desc: "Drone capacity +30%", cost: 1300, requires: ["hangar1"], col: 1, row: 6, effect: { droneCargoMul: 1.3 } },
  { id: "hangar2", name: "Hangar Bay II", desc: "+1 drone", cost: 2600, requires: ["hangar1"], col: 2, row: 6, effect: { hangar: 1 } },
  { id: "dmine1", name: "Plasma Drills I", desc: "Drone mining +30%", cost: 1300, requires: ["hangar1"], col: 3, row: 6, effect: { droneRateMul: 1.3 } },
  { id: "refine5", name: "Refinery IV", desc: "Mineral yield +30%", cost: 3200, sp: 2, requires: ["refine4"], col: 4, row: 6, effect: { yieldMul: 1.3 } },

  // Row 7 — tier 2.
  { id: "dspeed2", name: "Drone Overdrive", desc: "Drone speed +25%", cost: 2200, requires: ["dspeed1"], col: 0, row: 7, effect: { droneSpeedMul: 1.25 } },
  { id: "dcargo2", name: "Cargo Pods II", desc: "Drone capacity +35%", cost: 2400, requires: ["dcargo1"], col: 1, row: 7, effect: { droneCargoMul: 1.35 } },
  { id: "hangar3", name: "Hangar Bay III", desc: "+1 drone", cost: 4200, requires: ["hangar2"], col: 2, row: 7, effect: { hangar: 1 } },
  { id: "dmine2", name: "Plasma Drills II", desc: "Drone mining +35%", cost: 2500, requires: ["dmine1"], col: 3, row: 7, effect: { droneRateMul: 1.35 } },

  // ============ MILESTONE — Mothership Mk III (MTS_3) ============
  {
    id: "mk3",
    name: "Mothership Mk III",
    desc: "Capital hull: mining +40%, range +30%, +1 drone, yield +25%, speed −10%.",
    cost: 100000,
    cores: 5,
    location: "mechanical",
    requires: ["hangar2", "dcargo2", "dmine2"],
    col: 2,
    row: 8,
    effect: { hullRateMul: 1.4, rangeMul: 1.3, hangar: 1, yieldMul: 1.25, shipSpeedMul: 0.75 },
    milestone: true,
  },

  // Solar Sail branch (col 3) — faster flight in the SOLAR layer only.
  { id: "solar1", name: "Solar Sail I", desc: "Solar-layer flight speed +40%", cost: 900, requires: ["engine3"], col: 3, row: 3, effect: { solarSpeedMul: 1.4 } },
  { id: "solar2", name: "Solar Sail II", desc: "Solar-layer flight speed +40%", cost: 2000, requires: ["solar1"], col: 3, row: 4, effect: { solarSpeedMul: 1.4 } },
  { id: "solar3", name: "Ramscoop Sails", desc: "Solar-layer flight speed +50%", cost: 4200, sp: 1, requires: ["solar2"], col: 3, row: 5, effect: { solarSpeedMul: 1.5 } },

  // Extra hull-era nodes.
  { id: "optic5", name: "Deep-Field Optics", desc: "Mining range +25%", cost: 1500, requires: ["optic4"], col: 1, row: 4, effect: { rangeMul: 1.25 } },
  { id: "refineA", name: "Electro-Refinery", desc: "Mineral yield +20%", cost: 900, sp: 1, requires: ["refine3"], col: 4, row: 3, effect: { yieldMul: 1.2 } },

  // Row 8 — tier 3 (drone deploy range / capacity / drills).
  { id: "drange1", name: "Deploy Range I", desc: "Drone deploy area +40%", cost: 2400, requires: ["dspeed2"], col: 0, row: 8, effect: { droneRangeMul: 1.4 } },
  { id: "dcargo3", name: "Cargo Pods III", desc: "Drone capacity +40%", cost: 3600, requires: ["dcargo2"], col: 1, row: 8, effect: { droneCargoMul: 1.4 } },
  { id: "dmine3", name: "Plasma Drills III", desc: "Drone mining +40%", cost: 3800, requires: ["dmine2"], col: 3, row: 8, effect: { droneRateMul: 1.4 } },

  // ============ ACT III — endgame (require Mk III) ============
  { id: "drange2", name: "Deploy Range II", desc: "Drone deploy area +40%", cost: 5200, requires: ["drange1"], col: 0, row: 9, effect: { droneRangeMul: 1.4 } },
  { id: "dcargo4", name: "Freight Holds", desc: "Drone capacity +50%", cost: 6000, requires: ["dcargo3"], col: 1, row: 9, effect: { droneCargoMul: 1.5 } },
  { id: "hangar4", name: "Swarm Command", desc: "+2 drones", cost: 6500, requires: ["mk3"], col: 2, row: 9, effect: { hangar: 2 } },
  { id: "laser5", name: "Siege Laser", desc: "Mining speed +40%", cost: 6800, requires: ["mk3"], col: 3, row: 9, effect: { hullRateMul: 1.4 } },
  { id: "refine6", name: "Nanite Refinery", desc: "Mineral yield +35%", cost: 7500, sp: 2, requires: ["refine5"], col: 4, row: 9, effect: { yieldMul: 1.35 } },
  { id: "engine4", name: "Warp Coils", desc: "Ship speed +30%", cost: 6000, requires: ["hangar4"], col: 2, row: 10, effect: { shipSpeedMul: 1.3 } },

  // ============ MILESTONE — Mothership Mk IV (MTS_4) ============
  {
    id: "mk4",
    name: "Mothership Mk IV",
    desc: "Carrier hull (2× size): plows asteroids, 8 turrets + 3 spinal cannons. Mining +30%, +2 drones, speed −50%.",
    cost: 500000,
    cores: 10,
    location: "mechanical",
    requires: ["mk3", "laser5"],
    col: 3,
    row: 10,
    effect: { hullRateMul: 1.3, rangeMul: 1.2, hangar: 2, shipSpeedMul: 0.6 },
    milestone: true,
  },
  // --- Mk IV-only branch (Battleship) ---
  {
    id: "omniMine",
    name: "Omni-Mining Array",
    desc: "Mine a full 360° around the hull (not just the front).",
    cost: 30000,
    cores: 1,
    location: "mechanical",
    requires: ["mk4"],
    col: 4,
    row: 10,
    effect: { omniMining: true },
  },
  // Two engine upgrades to claw back the Mk IV hull's speed penalty (+10% each).
  {
    id: "mk4speed1",
    name: "Afterburner I",
    desc: "Ship speed +10%",
    cost: 22000,
    cores: 1,
    location: "mechanical",
    requires: ["mk4"],
    col: 1,
    row: 11,
    effect: { shipSpeedMul: 1.1 },
  },
  {
    id: "mk4speed2",
    name: "Afterburner II",
    desc: "Ship speed +10%",
    cost: 40000,
    cores: 2,
    location: "mechanical",
    requires: ["mk4speed1"],
    col: 1,
    row: 12,
    effect: { shipSpeedMul: 1.1 },
  },
  {
    id: "fleet1",
    name: "Fighter Bay I",
    desc: "+1 escort-fighter slot.",
    cost: 25000,
    cores: 1,
    location: "mechanical",
    requires: ["mk4"],
    col: 4,
    row: 11,
    effect: { fighterSlots: 1 },
  },
  {
    id: "fleet2",
    name: "Fighter Bay II",
    desc: "+2 escort-fighter slots.",
    cost: 40000,
    cores: 1,
    location: "mechanical",
    requires: ["fleet1"],
    col: 4,
    row: 12,
    effect: { fighterSlots: 2 },
  },
  {
    id: "fleet3",
    name: "Fighter Bay III",
    desc: "+2 escort-fighter slots.",
    cost: 60000,
    cores: 2,
    location: "mechanical",
    requires: ["fleet2"],
    col: 4,
    row: 13,
    effect: { fighterSlots: 2 },
  },
  {
    id: "fleet4",
    name: "Fighter Wing",
    desc: "+3 escort-fighter slots (8 total).",
    cost: 90000,
    cores: 3,
    location: "mechanical",
    requires: ["fleet3"],
    col: 4,
    row: 14,
    effect: { fighterSlots: 3 },
  },

  // --- Repair-drone expansions (hull-tier gated) ---
  // Each adds +2 mechanical repair drones on top of the base Repair Drone Bay.
  {
    id: "repair2",
    name: "Repair Drone Bay II",
    desc: "+2 repair drones",
    cost: 5500,
    location: "mechanical",
    requires: ["mk3"],
    col: 2,
    row: 11,
    effect: { mechDrones: 2 },
  },
  {
    id: "repair3",
    name: "Repair Drone Bay III",
    desc: "+2 repair drones",
    cost: 12000,
    location: "mechanical",
    requires: ["mk4"],
    col: 3,
    row: 11,
    effect: { mechDrones: 2 },
  },

  // ============ MILESTONE — Mothership Mk V (choose ONE path) ============
  // Two mutually-exclusive refits off the Carrier. Buying one permanently locks
  // the other (`excludes`). Same cost; the paths diverge in later phases.
  {
    id: "mkv_bts",
    name: "Mk V · Battleship",
    desc: "Firepower path: 14 turrets, 4 cannons + 2 map-length Big Cannons, 8 fighters. Shoots to mine.",
    cost: 1_000_000,
    units: 5_000,
    cores: 10,
    location: "mechanical",
    requires: ["mk4"],
    excludes: ["mkv_swl"],
    col: 1,
    row: 13,
    effect: {},
    milestone: true,
  },
  {
    id: "mkv_swl",
    name: "Mk V · Swarm-lord",
    desc: "Swarm path: 12 fighters + 12 fighter drones, 14 turrets, 2 cannons, wing runways + defensive rings.",
    cost: 1_000_000,
    units: 5_000,
    cores: 10,
    location: "mechanical",
    requires: ["mk4"],
    excludes: ["mkv_bts"],
    col: 3,
    row: 13,
    effect: {},
    milestone: true,
  },
  // --- Swarm-lord-only branch: fighter-drone bays (grow the drone swarm to 12) ---
  {
    id: "dronebay1",
    name: "Drone Swarm I",
    desc: "Unlocks fighter drones: +4 (smaller, faster, weaker; buffed by the Fighter Drone Controller).",
    cost: 60000,
    cores: 2,
    location: "mechanical",
    requires: ["mkv_swl"],
    col: 3,
    row: 14,
    effect: { fighterDrones: 4 },
  },
  {
    id: "dronebay2",
    name: "Drone Swarm II",
    desc: "+4 fighter drones (8 total).",
    cost: 110000,
    cores: 3,
    location: "mechanical",
    requires: ["dronebay1"],
    col: 3,
    row: 15,
    effect: { fighterDrones: 4 },
  },
  {
    id: "dronebay3",
    name: "Drone Swarm III",
    desc: "+4 fighter drones (12 total — the swarm cap).",
    cost: 180000,
    cores: 4,
    location: "mechanical",
    requires: ["dronebay2"],
    col: 3,
    row: 16,
    effect: { fighterDrones: 4 },
  },
  // Swarm-lord homing-missile-salvo skill (right-rail button, 20s cooldown).
  {
    id: "missile1",
    name: "Missile Pod I",
    desc: "Unlocks the homing-missile salvo: +4 missiles per salvo (each chases a different enemy).",
    cost: 70000,
    cores: 2,
    location: "mechanical",
    requires: ["mkv_swl"],
    col: 4,
    row: 15,
    effect: { missiles: 4 },
  },
  {
    id: "missile2",
    name: "Missile Pod II",
    desc: "+4 missiles per salvo (8 total).",
    cost: 120000,
    cores: 3,
    location: "mechanical",
    requires: ["missile1"],
    col: 4,
    row: 16,
    effect: { missiles: 4 },
  },
  {
    id: "missile3",
    name: "Missile Pod III",
    desc: "+4 missiles per salvo (12 total).",
    cost: 190000,
    cores: 4,
    location: "mechanical",
    requires: ["missile2"],
    col: 4,
    row: 17,
    effect: { missiles: 4 },
  },
  {
    id: "missile4",
    name: "Missile Pod IV",
    desc: "+4 missiles per salvo (16 total — the Mk V cap).",
    cost: 280000,
    cores: 5,
    location: "mechanical",
    requires: ["missile3"],
    col: 4,
    row: 18,
    effect: { missiles: 4 },
  },
  // --- Battleship-only branches: turret range / speed / hull HP (2 tiers each) ---
  {
    id: "btsrange1",
    name: "Targeting Array I",
    desc: "Turret firing range +10%",
    cost: 45000,
    cores: 2,
    location: "mechanical",
    requires: ["mkv_bts"],
    col: 0,
    row: 14,
    effect: { turretRangeMul: 1.1 },
  },
  {
    id: "btsrange2",
    name: "Targeting Array II",
    desc: "Turret firing range +10%",
    cost: 80000,
    cores: 3,
    location: "mechanical",
    requires: ["btsrange1"],
    col: 0,
    row: 15,
    effect: { turretRangeMul: 1.1 },
  },
  {
    id: "btsspeed1",
    name: "Battleship Thrusters I",
    desc: "Ship speed +10%",
    cost: 45000,
    cores: 2,
    location: "mechanical",
    requires: ["mkv_bts"],
    col: 1,
    row: 14,
    effect: { shipSpeedMul: 1.1 },
  },
  {
    id: "btsspeed2",
    name: "Battleship Thrusters II",
    desc: "Ship speed +10%",
    cost: 80000,
    cores: 3,
    location: "mechanical",
    requires: ["btsspeed1"],
    col: 1,
    row: 15,
    effect: { shipSpeedMul: 1.1 },
  },
  {
    id: "btshp1",
    name: "Reinforced Plating I",
    desc: "Max hull HP +100",
    cost: 45000,
    cores: 2,
    location: "mechanical",
    requires: ["mkv_bts"],
    col: 2,
    row: 14,
    effect: { hullHpBonus: 100 },
  },
  {
    id: "btshp2",
    name: "Reinforced Plating II",
    desc: "Max hull HP +100",
    cost: 80000,
    cores: 3,
    location: "mechanical",
    requires: ["btshp1"],
    col: 2,
    row: 15,
    effect: { hullHpBonus: 100 },
  },

  // ============ MILESTONE — Mothership Mk VI · Battleship ============
  // The Battleship line's next hull: bigger armour, 18 turrets, 8 spinal cannons,
  // 4 Big Cannons (2 per flank) and a 6-silo homing-missile salvo. Refit off Mk V.
  {
    id: "mkvi_bts",
    name: "Mk VI · Battleship",
    desc: "Bigger Battleship: 18 turrets, 8 cannons, 4 Big Cannons (2 per side), 6 missile silos, +250 shield.",
    cost: 5_000_000,
    units: 25_000,
    cores: 30,
    location: "mechanical",
    requires: ["mkv_bts"],
    col: 1,
    row: 16,
    effect: {},
    milestone: true,
  },
  // The Swarm-lord line's next hull: a bigger drone carrier with 4 wing hangars, a larger
  // swarm, light turrets and cannons, and +150 shield. Refit off Mk V Swarm-lord.
  {
    id: "mkvi_swl",
    name: "Mk VI · Swarm-lord",
    desc: "Bigger Swarm-lord carrier: 4 wing hangars, up to 28 drones, 16 fighters, 11 turrets, 8 cannons, +150 shield. Unlocks the Mk VI branches (wing missiles, drone lances, smart missiles, +40% speed & more). No Big Cannons.",
    cost: 5_000_000,
    units: 25_000,
    cores: 30,
    location: "mechanical",
    requires: ["mkv_swl"],
    col: 3,
    row: 17,
    effect: {},
    milestone: true,
  },
  // --- Mk VI Swarm-lord: extra drone bays (grows the swarm past the Mk V cap, up to 18) ---
  {
    id: "mkvi_dronebay1",
    name: "Drone Swarm IV",
    desc: "+3 fighter drones for the swarm.",
    cost: 300000,
    cores: 5,
    location: "mechanical",
    requires: ["mkvi_swl"],
    col: 3,
    row: 18,
    effect: { fighterDrones: 3 },
  },
  {
    id: "mkvi_dronebay2",
    name: "Drone Swarm V",
    desc: "+3 more fighter drones (18 so far; the Drone Swarm VI–VII nodes raise the cap to 28).",
    cost: 500000,
    cores: 6,
    location: "mechanical",
    requires: ["mkvi_dronebay1"],
    col: 3,
    row: 19,
    effect: { fighterDrones: 3 },
  },
  // --- Mk VI Battleship: Missile Silo salvo (grows the 6-silo salvo 2 → 6 → 10 → 14) ---
  {
    id: "mkvi_missile1",
    name: "Missile Silos I",
    desc: "Unlocks the homing-missile salvo: +2 missiles (each chases a different enemy).",
    cost: 200000,
    cores: 4,
    location: "mechanical",
    requires: ["mkvi_bts"],
    col: 1,
    row: 17,
    effect: { siloMissiles: 2 },
  },
  {
    id: "mkvi_missile2",
    name: "Missile Silos II",
    desc: "+4 missiles per salvo (6 total).",
    cost: 350000,
    cores: 5,
    location: "mechanical",
    requires: ["mkvi_missile1"],
    col: 1,
    row: 18,
    effect: { siloMissiles: 4 },
  },
  {
    id: "mkvi_missile3",
    name: "Missile Silos III",
    desc: "+4 missiles per salvo (10 total).",
    cost: 550000,
    cores: 6,
    location: "mechanical",
    requires: ["mkvi_missile2"],
    col: 1,
    row: 19,
    effect: { siloMissiles: 4 },
  },
  {
    id: "mkvi_missile4",
    name: "Missile Silos IV",
    desc: "+4 missiles per salvo (14 total — the Mk VI cap).",
    cost: 800000,
    cores: 8,
    location: "mechanical",
    requires: ["mkvi_missile3"],
    col: 1,
    row: 20,
    effect: { siloMissiles: 4 },
  },
  // --- Mk VI Battleship: Big-Cannon beam-DURATION upgrades (+5% each, multiplicative) ---
  {
    id: "mkvi_beam1",
    name: "Beam Capacitors I",
    desc: "Big Cannon beams last 5% longer.",
    cost: 300000,
    cores: 4,
    location: "mechanical",
    requires: ["mkvi_bts"],
    col: 2,
    row: 17,
    effect: { bigCannonBeamMul: 1.05 },
  },
  {
    id: "mkvi_beam2",
    name: "Beam Capacitors II",
    desc: "Big Cannon beams last another 5% longer.",
    cost: 500000,
    cores: 5,
    location: "mechanical",
    requires: ["mkvi_beam1"],
    col: 2,
    row: 18,
    effect: { bigCannonBeamMul: 1.05 },
  },
  {
    id: "mkvi_beam3",
    name: "Beam Capacitors III",
    desc: "Big Cannon beams last another 5% longer.",
    cost: 750000,
    cores: 6,
    location: "mechanical",
    requires: ["mkvi_beam2"],
    col: 2,
    row: 19,
    effect: { bigCannonBeamMul: 1.05 },
  },
  // ============ SECRET MILESTONE — Mk VII · Eater of Worlds ============
  // Hidden until the player holds the EOW parts (which the refit consumes). Turns the
  // ship into a worm: passthrough mining, a maw beam, one turret per segment.
  {
    id: "mkvii",
    name: "Mk VII · Eater of Worlds",
    desc: "SECRET: become the Eater of Worlds. Mine by passing THROUGH rocks/moons; fire a high-damage maw beam; one turret per segment; no thrusters. Consumes 1 EOW Head, 10 Segments, 1 Tail, 1 Core.",
    cost: 20_000_000,
    units: 100_000,
    location: "mechanical",
    requires: ["mk4"],
    requiresItems: { eowHead: 1, eowBody: 10, eowTail: 1, eowEnergyCore: 1 },
    col: 0,
    row: 16,
    effect: { hullHpBonus: 4000 },
    milestone: true,
  },
  {
    id: "mkvii_seg1",
    name: "Extra Segments I",
    desc: "+4 body segments — more turrets and a longer body to graze rocks with.",
    cost: 2_000_000,
    cores: 5,
    location: "mechanical",
    requires: ["mkvii"],
    col: 0,
    row: 17,
    effect: { wormBodies: 4 },
  },
  {
    id: "mkvii_seg2",
    name: "Extra Segments II",
    desc: "+4 body segments.",
    cost: 4_000_000,
    cores: 7,
    location: "mechanical",
    requires: ["mkvii_seg1"],
    col: 0,
    row: 18,
    effect: { wormBodies: 4 },
  },
  {
    id: "mkvii_seg3",
    name: "Extra Segments III",
    desc: "+4 body segments (maximum body length).",
    cost: 7_000_000,
    cores: 9,
    location: "mechanical",
    requires: ["mkvii_seg2"],
    col: 0,
    row: 19,
    effect: { wormBodies: 4 },
  },
  // Mk VII missile branch — a RED homing salvo from the mouth (max 20).
  {
    id: "mkvii_missile1",
    name: "Devouring Missiles I",
    desc: "Unlocks a RED homing-missile salvo — 5 missiles fire from the mouth sides, straight for a beat then homing.",
    cost: 2_000_000,
    cores: 5,
    location: "mechanical",
    requires: ["mkvii"],
    col: 1,
    row: 21,
    effect: { wormMissiles: 5 },
  },
  {
    id: "mkvii_missile2",
    name: "Devouring Missiles II",
    desc: "+5 missiles per salvo (10 total).",
    cost: 4_000_000,
    cores: 6,
    location: "mechanical",
    requires: ["mkvii_missile1"],
    col: 1,
    row: 22,
    effect: { wormMissiles: 5 },
  },
  {
    id: "mkvii_missile3",
    name: "Devouring Missiles III",
    desc: "+5 missiles per salvo (15 total).",
    cost: 6_000_000,
    cores: 7,
    location: "mechanical",
    requires: ["mkvii_missile2"],
    col: 1,
    row: 23,
    effect: { wormMissiles: 5 },
  },
  {
    id: "mkvii_missile4",
    name: "Devouring Missiles IV",
    desc: "+5 missiles per salvo (20 — the maximum).",
    cost: 8_000_000,
    cores: 8,
    location: "mechanical",
    requires: ["mkvii_missile3"],
    col: 1,
    row: 24,
    effect: { wormMissiles: 5 },
  },
  // Mk VII maw-beam DAMAGE branch.
  {
    id: "mkvii_beamdmg1",
    name: "Maw Focus I",
    desc: "The maw beam (main + every split) does +15% damage.",
    cost: 3_000_000,
    cores: 5,
    location: "mechanical",
    requires: ["mkvii"],
    col: 2,
    row: 21,
    effect: { wormBeamDmgMul: 1.15 },
  },
  {
    id: "mkvii_beamdmg2",
    name: "Maw Focus II",
    desc: "+15% maw-beam damage.",
    cost: 5_000_000,
    cores: 6,
    location: "mechanical",
    requires: ["mkvii_beamdmg1"],
    col: 2,
    row: 22,
    effect: { wormBeamDmgMul: 1.15 },
  },
  {
    id: "mkvii_beamdmg3",
    name: "Maw Focus III",
    desc: "+15% maw-beam damage.",
    cost: 8_000_000,
    cores: 7,
    location: "mechanical",
    requires: ["mkvii_beamdmg2"],
    col: 2,
    row: 23,
    effect: { wormBeamDmgMul: 1.15 },
  },
  // Mk VII maw-beam DURATION branch.
  {
    id: "mkvii_beamdur1",
    name: "Maw Capacitor I",
    desc: "The maw beam's big + fan phases last +20% longer.",
    cost: 3_000_000,
    cores: 5,
    location: "mechanical",
    requires: ["mkvii"],
    col: 3,
    row: 21,
    effect: { wormBeamDurMul: 1.2 },
  },
  {
    id: "mkvii_beamdur2",
    name: "Maw Capacitor II",
    desc: "+20% maw-beam duration.",
    cost: 5_000_000,
    cores: 6,
    location: "mechanical",
    requires: ["mkvii_beamdur1"],
    col: 3,
    row: 22,
    effect: { wormBeamDurMul: 1.2 },
  },
  {
    id: "mkvii_beamdur3",
    name: "Maw Capacitor III",
    desc: "+20% maw-beam duration.",
    cost: 8_000_000,
    cores: 7,
    location: "mechanical",
    requires: ["mkvii_beamdur2"],
    col: 3,
    row: 23,
    effect: { wormBeamDurMul: 1.2 },
  },
  // Mk VII SPEED branch — buyable ANYWHERE (no mechanical yard).
  {
    id: "mkvii_speed1",
    name: "Undulation I",
    desc: "The worm slithers +15% faster. Buyable anywhere — no mechanical yard needed.",
    cost: 1_500_000,
    units: 3_000,
    requires: ["mkvii"],
    col: 0,
    row: 21,
    effect: { shipSpeedMul: 1.15 },
  },
  {
    id: "mkvii_speed2",
    name: "Undulation II",
    desc: "+15% worm speed (no mechanical yard needed).",
    cost: 3_000_000,
    units: 6_000,
    requires: ["mkvii_speed1"],
    col: 0,
    row: 22,
    effect: { shipSpeedMul: 1.15 },
  },
  {
    id: "mkvii_speed3",
    name: "Undulation III",
    desc: "+15% worm speed (no mechanical yard needed).",
    cost: 5_000_000,
    units: 10_000,
    requires: ["mkvii_speed2"],
    col: 0,
    row: 23,
    effect: { shipSpeedMul: 1.15 },
  },
  // Mk VII BRANCHING missiles — under the worm's missile chain.
  {
    id: "mkvii_branch1",
    name: "Splitting Warheads I",
    desc: "Each red missile that hits a target has a 10% chance to split into 1 branch missile that finds a new target.",
    cost: 4_000_000,
    cores: 6,
    location: "mechanical",
    requires: ["mkvii_missile4"],
    col: 1,
    row: 25,
    effect: { branchChance: 0.1, branchCount: 1 },
  },
  {
    id: "mkvii_branch2",
    name: "Splitting Warheads II",
    desc: "+5% split chance and +2 branch missiles.",
    cost: 6_000_000,
    cores: 7,
    location: "mechanical",
    requires: ["mkvii_branch1"],
    col: 1,
    row: 26,
    effect: { branchChance: 0.05, branchCount: 2 },
  },
  {
    id: "mkvii_branch3",
    name: "Splitting Warheads III",
    desc: "+5% split chance (20% max) and +2 branch missiles (5 max).",
    cost: 9_000_000,
    cores: 8,
    location: "mechanical",
    requires: ["mkvii_branch2"],
    col: 1,
    row: 27,
    effect: { branchChance: 0.05, branchCount: 2 },
  },
  // Swarm-lord BRANCHING missiles — under the Missile Pod chain.
  {
    id: "swl_branch1",
    name: "Splitting Pods I",
    desc: "Each missile that hits a target has a 10% chance to split into 1 branch missile that finds a new target.",
    cost: 400000,
    cores: 5,
    location: "mechanical",
    requires: ["missile4"],
    col: 4,
    row: 19,
    effect: { branchChance: 0.1, branchCount: 1 },
  },
  {
    id: "swl_branch2",
    name: "Splitting Pods II",
    desc: "+5% split chance and +2 branch missiles.",
    cost: 700000,
    cores: 6,
    location: "mechanical",
    requires: ["swl_branch1"],
    col: 4,
    row: 20,
    effect: { branchChance: 0.05, branchCount: 2 },
  },
  {
    id: "swl_branch3",
    name: "Splitting Pods III",
    desc: "+5% split chance (20% max) and +2 branch missiles (5 max).",
    cost: 1100000,
    cores: 7,
    location: "mechanical",
    requires: ["swl_branch2"],
    col: 4,
    row: 21,
    effect: { branchChance: 0.05, branchCount: 2 },
  },

  // ============ Mk VI Swarm-lord branches (cols 5-7, all gated on mkvi_swl) ============
  // Missile Pods V–VII — grow the salvo 16 → 28.
  { id: "swlvi_missilepod1", name: "Missile Pod V", desc: "+4 missiles per salvo (20 total).", cost: 400000, cores: 5, location: "mechanical", requires: ["missile4", "mkvi_swl"], col: 5, row: 14, effect: { missiles: 4 } },
  { id: "swlvi_missilepod2", name: "Missile Pod VI", desc: "+4 missiles per salvo (24 total).", cost: 600000, cores: 6, location: "mechanical", requires: ["swlvi_missilepod1"], col: 5, row: 15, effect: { missiles: 4 } },
  { id: "swlvi_missilepod3", name: "Missile Pod VII", desc: "+4 missiles per salvo (28 total — the Mk VI cap).", cost: 850000, cores: 7, location: "mechanical", requires: ["swlvi_missilepod2"], col: 5, row: 16, effect: { missiles: 4 } },
  // Rapid Salvo I–III — −15% missile cooldown each (multiplicative).
  { id: "swlvi_missilecd1", name: "Rapid Salvo I", desc: "−15% missile-salvo cooldown.", cost: 350000, cores: 5, location: "mechanical", requires: ["mkvi_swl"], col: 5, row: 18, effect: { missileCdMul: 0.85 } },
  { id: "swlvi_missilecd2", name: "Rapid Salvo II", desc: "−15% missile-salvo cooldown.", cost: 550000, cores: 6, location: "mechanical", requires: ["swlvi_missilecd1"], col: 5, row: 19, effect: { missileCdMul: 0.85 } },
  { id: "swlvi_missilecd3", name: "Rapid Salvo III", desc: "−15% missile-salvo cooldown (~12s at full stack).", cost: 800000, cores: 7, location: "mechanical", requires: ["swlvi_missilecd2"], col: 5, row: 20, effect: { missileCdMul: 0.85 } },
  // Smart Missiles — re-acquire a new target if the original dies.
  { id: "swlvi_smartmissile", name: "Smart Missiles", desc: "A missile whose target is destroyed locks onto a new nearest enemy instead of wasting the shot.", cost: 900000, cores: 8, location: "mechanical", requires: ["mkvi_swl"], col: 5, row: 22, effect: { smartMissiles: true } },
  // Splitting Pods IV–V — extend the branch caps past Mk V (→ 35% / 8).
  { id: "swlvi_branch4", name: "Splitting Pods IV", desc: "+5% split chance and +1 branch missile.", cost: 1400000, cores: 8, location: "mechanical", requires: ["swl_branch3", "mkvi_swl"], col: 5, row: 24, effect: { branchChance: 0.05, branchCount: 1 } },
  { id: "swlvi_branch5", name: "Splitting Pods V", desc: "+10% split chance (35% max) and +2 branch missiles (8 max).", cost: 2000000, cores: 9, location: "mechanical", requires: ["swlvi_branch4"], col: 5, row: 25, effect: { branchChance: 0.1, branchCount: 2 } },
  // Drone Swarm VI–VII — grow the drone cap 18 → 28.
  { id: "mkvi_dronebay3", name: "Drone Swarm VI", desc: "+5 fighter drones (23 total).", cost: 700000, cores: 7, location: "mechanical", requires: ["mkvi_dronebay2"], col: 6, row: 14, effect: { fighterDrones: 5 } },
  { id: "mkvi_dronebay4", name: "Drone Swarm VII", desc: "+5 fighter drones (28 total — the Mk VI cap).", cost: 1000000, cores: 8, location: "mechanical", requires: ["mkvi_dronebay3"], col: 6, row: 15, effect: { fighterDrones: 5 } },
  // Drone Lance — each drone fires a short combat beam (mining is a bonus).
  { id: "swlvi_dronebeam", name: "Drone Missiles", desc: "Each fighter drone auto-launches a homing missile at the nearest enemy (1 missile, 15s cooldown) — the same missile the escort fighters fire.", cost: 1600000, cores: 10, location: "mechanical", requires: ["mkvi_swl"], col: 6, row: 17, effect: { droneMissiles: true } },
  // Fighter Wings V–VI — +4 escort-fighter bays (12 → 16).
  { id: "swlvi_fbay1", name: "Fighter Wing V", desc: "+2 escort-fighter bays.", cost: 500000, cores: 6, location: "mechanical", requires: ["mkvi_swl"], col: 6, row: 19, effect: { fighterSlots: 2 } },
  { id: "swlvi_fbay2", name: "Fighter Wing VI", desc: "+2 escort-fighter bays (16 total).", cost: 750000, cores: 7, location: "mechanical", requires: ["swlvi_fbay1"], col: 6, row: 20, effect: { fighterSlots: 2 } },
  // Wing Missiles — escort fighters auto-fire homing missiles.
  { id: "swlvi_fmissile", name: "Wing Missiles", desc: "Each escort fighter auto-launches 2 homing missiles at the nearest enemy (15s cooldown).", cost: 1600000, cores: 10, location: "mechanical", requires: ["mkvi_swl"], col: 6, row: 22, effect: { fighterMissiles: true } },
  // Swarm Thrusters I–II — +40% ship speed.
  { id: "swlvi_speed1", name: "Swarm Thrusters I", desc: "+20% ship speed.", cost: 500000, cores: 5, location: "mechanical", requires: ["mkvi_swl"], col: 7, row: 14, effect: { swlSpeedBonus: 0.2 } },
  { id: "swlvi_speed2", name: "Swarm Thrusters II", desc: "+20% ship speed (+40% total).", cost: 800000, cores: 6, location: "mechanical", requires: ["swlvi_speed1"], col: 7, row: 15, effect: { swlSpeedBonus: 0.2 } },
  // Mk VII Swarm-lord — the cosmic apex refit off the Mk VI Swarm-lord (tier-5 swarmlord variant).
  { id: "mkvii_swl", name: "Mk VII · Swarm-lord", desc: "The cosmic apex of the Swarm-lord line — a far larger carrier hull (+20% size, +250 shield) with a new mount layout, keeping the full Mk VI swarm arsenal. Refit off the Mk VI Swarm-lord.", cost: 15_000_000, units: 50_000, cores: 50, location: "mechanical", requires: ["mkvi_swl"], col: 3, row: 20, effect: {}, milestone: true },

  // ============ SECRET MILESTONE — Mk VIII · Mechanical Eater of Worlds ============
  // Hidden until the player OWNS Mk VII. A mechanical evolution of the worm: tougher hull,
  // a stronger maw beam, and a longer body.
  {
    id: "mkviii",
    name: "Mk VIII · Mechanical Eater of Worlds",
    desc: "SECRET evolution of the Eater of Worlds — a mechanical worm with a tougher hull, a stronger maw beam, and +4 body segments. Refit off Mk VII.",
    cost: 40_000_000,
    units: 200_000,
    cores: 40,
    location: "mechanical",
    requires: ["mkvii"],
    hiddenUntil: "mkvii",
    col: 2,
    row: 16,
    effect: { hullHpBonus: 4000, wormBeamDmgMul: 1.4, wormBodies: 4 },
    milestone: true,
  },
  // Time & Space Rift — the Mk VIII's signature teleport ability. Hidden until Mk VIII is owned.
  {
    id: "mkviii_rift",
    name: "Time & Space Rift",
    desc: "SECRET Mk VIII ability: arm it, then tap the minimap. A sweeping laser carves a rift open (damaging foes + mining rocks); the worm dives through and re-emerges at the tapped spot.",
    cost: 20_000_000,
    units: 120_000,
    cores: 25,
    location: "mechanical",
    requires: ["mkviii"],
    hiddenUntil: "mkviii",
    col: 4,
    row: 22,
    effect: {},
  },
  // Maelstrom Coil — the Mk VIII's defensive/AoE stance. Hidden until Mk VIII is owned.
  {
    id: "mkviii_coil",
    name: "Maelstrom Coil",
    desc: "SECRET Mk VIII ability: coil the worm into a spinning maelstrom — every segment fires a sweeping laser (½ the maw beam) while a 700-HP shield bubble covers the coil and sears anything inside. Rooted for ~3s.",
    cost: 24_000_000,
    units: 140_000,
    cores: 28,
    location: "mechanical",
    requires: ["mkviii"],
    hiddenUntil: "mkviii",
    col: 4,
    row: 23,
    effect: {},
  },
];

export const UPGRADE_COLS = 8; // cols 5-7 hold the Mk VI Swarm-lord branches
export const UPGRADE_ROWS = 28;

export const UPGRADES_BY_ID: Record<string, UpgradeNode> = Object.fromEntries(
  UPGRADES.map((n) => [n.id, n]),
);

export interface Mods {
  hangar: number;
  mechDrones: number;
  beams: number;
  droneSpeedMul: number;
  droneCargoMul: number;
  droneRateMul: number;
  droneRangeMul: number;
  shipSpeedMul: number;
  solarSpeedMul: number;
  rangeMul: number;
  hullRateMul: number;
  yieldMul: number;
  fighterSlots: number;
  fighterDrones: number;
  missiles: number;
  siloMissiles: number;
  turretRangeMul: number;
  hullHpBonus: number;
  bigCannonBeamMul: number;
  omniMining: boolean;
  wormBodies: number;
  wormMissiles: number;
  wormBeamDurMul: number;
  wormBeamDmgMul: number;
  branchChance: number;
  branchCount: number;
  missileCdMul: number;
  swlSpeedBonus: number;
  fighterMissiles: boolean;
  droneMissiles: boolean;
  smartMissiles: boolean;
}

/** Fold the purchased set into a single modifier bundle. */
export function computeMods(purchased: ReadonlySet<string>): Mods {
  const m: Mods = {
    hangar: 0,
    mechDrones: 0,
    beams: 0,
    droneSpeedMul: 1,
    droneCargoMul: 1,
    droneRateMul: 1,
    droneRangeMul: 1,
    shipSpeedMul: 1,
    solarSpeedMul: 1,
    rangeMul: 1,
    hullRateMul: 1,
    yieldMul: 1,
    fighterSlots: 0,
    fighterDrones: 0,
    missiles: 0,
    siloMissiles: 0,
    turretRangeMul: 1,
    hullHpBonus: 0,
    bigCannonBeamMul: 1,
    omniMining: false,
    wormBodies: 0,
    wormMissiles: 0,
    wormBeamDurMul: 1,
    wormBeamDmgMul: 1,
    branchChance: 0,
    branchCount: 0,
    missileCdMul: 1,
    swlSpeedBonus: 0,
    fighterMissiles: false,
    droneMissiles: false,
    smartMissiles: false,
  };
  for (const n of UPGRADES) {
    if (!purchased.has(n.id)) continue;
    const e = n.effect;
    m.fighterSlots += e.fighterSlots ?? 0;
    m.fighterDrones += e.fighterDrones ?? 0;
    m.missiles += e.missiles ?? 0;
    m.siloMissiles += e.siloMissiles ?? 0;
    m.turretRangeMul *= e.turretRangeMul ?? 1;
    m.hullHpBonus += e.hullHpBonus ?? 0;
    m.bigCannonBeamMul *= e.bigCannonBeamMul ?? 1;
    m.omniMining = m.omniMining || (e.omniMining ?? false);
    m.wormBodies += e.wormBodies ?? 0;
    m.wormMissiles += e.wormMissiles ?? 0;
    m.wormBeamDurMul *= e.wormBeamDurMul ?? 1;
    m.wormBeamDmgMul *= e.wormBeamDmgMul ?? 1;
    m.branchChance += e.branchChance ?? 0;
    m.branchCount += e.branchCount ?? 0;
    m.missileCdMul *= e.missileCdMul ?? 1;
    m.swlSpeedBonus += e.swlSpeedBonus ?? 0;
    m.fighterMissiles = m.fighterMissiles || (e.fighterMissiles ?? false);
    m.droneMissiles = m.droneMissiles || (e.droneMissiles ?? false);
    m.smartMissiles = m.smartMissiles || (e.smartMissiles ?? false);
    m.hangar += e.hangar ?? 0;
    m.mechDrones += e.mechDrones ?? 0;
    m.beams += e.beams ?? 0;
    m.droneSpeedMul *= e.droneSpeedMul ?? 1;
    m.droneCargoMul *= e.droneCargoMul ?? 1;
    m.droneRateMul *= e.droneRateMul ?? 1;
    m.droneRangeMul *= e.droneRangeMul ?? 1;
    m.shipSpeedMul *= e.shipSpeedMul ?? 1;
    m.solarSpeedMul *= e.solarSpeedMul ?? 1;
    m.rangeMul *= e.rangeMul ?? 1;
    m.hullRateMul *= e.hullRateMul ?? 1;
    m.yieldMul *= e.yieldMul ?? 1;
  }
  return m;
}

export type NodeState = "owned" | "available" | "locked";

/** UI state of a node given the purchased set. */
export function nodeState(
  node: UpgradeNode,
  purchased: ReadonlySet<string>,
): NodeState {
  if (purchased.has(node.id)) return "owned";
  // A mutually-exclusive sibling was already taken → this path is locked out.
  if (node.excludes?.some((x) => purchased.has(x))) return "locked";
  if (node.requires.every((r) => purchased.has(r))) return "available";
  return "locked";
}

/** Whether a node can be bought right now. */
export function canPurchase(
  node: UpgradeNode,
  purchased: ReadonlySet<string>,
  minerals: number,
  sp = Infinity,
  units = Infinity,
): boolean {
  return (
    nodeState(node, purchased) === "available" &&
    minerals >= node.cost &&
    sp >= (node.sp ?? 0) &&
    units >= (node.units ?? 0)
  );
}
